<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import RefundsAPI from 'dashboard/api/refunds';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  refund: { type: Object, required: true },
});

const { t } = useI18n();
const dialogRef = ref(null);
const isUndoing = ref(false);
const isUndone = ref(false);

const formatDate = isoDate => {
  const [year, month, day] = isoDate.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

const description = computed(() => {
  const { status, due_on: dueOn, analysis_deadline: deadline } = props.refund;
  if (status === 'approved' && dueOn) {
    return t('REFUND_PANEL.APPROVED', { date: formatDate(dueOn) });
  }
  if (deadline) {
    return t('REFUND_PANEL.ANALYSIS', { date: formatDate(deadline) });
  }
  return t('REFUND_PANEL.ANALYSIS_NO_DATE');
});

const notionUrl = computed(() => {
  const pageId = props.refund.notion_page_id;
  return pageId ? `https://www.notion.so/${pageId.replace(/-/g, '')}` : null;
});

const undo = async () => {
  isUndoing.value = true;
  try {
    await RefundsAPI.undo(props.conversationId);
    isUndone.value = true;
    dialogRef.value.close();
    useAlert(t('REFUND_PANEL.UNDONE'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('REFUND_PANEL.UNDO_ERROR'));
  } finally {
    isUndoing.value = false;
  }
};
</script>

<template>
  <div
    v-if="!isUndone"
    class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak"
  >
    <div class="flex items-center gap-1.5 text-xs text-n-slate-10">
      <Icon icon="i-lucide-hand-coins" class="size-3.5" />
      {{ t('REFUND_PANEL.TITLE') }}
    </div>
    <div class="text-sm font-medium text-n-slate-12">{{ description }}</div>
    <div class="text-xs text-n-slate-11">
      <span v-if="refund.service_id">
        {{ t('REFUND_PANEL.SERVICE', { id: refund.service_id }) }}
      </span>
      <a
        v-if="notionUrl"
        :href="notionUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="ms-2 text-n-brand"
      >
        {{ t('REFUND_PANEL.OPEN_NOTION') }}
      </a>
    </div>
    <div v-if="refund.notion_error" class="text-xs text-n-ruby-11">
      {{ t('REFUND_PANEL.NOTION_ERROR') }}
    </div>
    <Button
      :label="t('REFUND_PANEL.UNDO')"
      icon="i-lucide-undo-2"
      slate
      faded
      xs
      class="self-start"
      @click="dialogRef.open()"
    />
    <Dialog
      ref="dialogRef"
      type="alert"
      :title="t('REFUND_PANEL.UNDO_TITLE')"
      :description="t('REFUND_PANEL.UNDO_DESCRIPTION')"
      :confirm-button-label="t('REFUND_PANEL.UNDO_CONFIRM')"
      :is-loading="isUndoing"
      @confirm="undo"
    />
  </div>
</template>
